/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    AzureBlobSwagger,
    AzureContainerSwagger,
    AzureCsAccountSwagger,
    AzureResourceGroupSwagger,
    AzureSearchServiceSwagger,
    AzureStorageAccountSwagger,
    AzureSubscriptionSwagger,
    AzureTenantSwagger,
    TokenSwagger
} from 'src/core/services/azure/azureSwaggers';
import { createAzureBlobContainerStore } from 'src/core/stores/AzureBlobContainerStore';
import { AzureBlobStore } from 'src/core/stores/AzureBlobStore';
import { AzureCsAccountProviders, AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { AzureResourceGroupStore } from 'src/core/stores/AzureResourceGroupStore';
import { AzureSearchStore } from 'src/core/stores/AzureSearchStore';
import { AzureStorageAccountStore } from 'src/core/stores/AzureStorageAccountStore';
import { AzureSubscriptionStore } from 'src/core/stores/AzureSubscriptionStore';
import { AzureTenantStore } from 'src/core/stores/AzureTenantStore';
import { Region } from 'src/core/types/regionTypes';
import { LuisConstants } from 'src/core/utils/LuisConstants';

/**
 * Creates an tenant store from the api received tenant object.
 *
 * @param azureTenantSwagger tenant api object received from the api.
 * @returns A tenant store item.
 */
export const tenantSwaggerToTenantStore = (swagger: AzureTenantSwagger) =>
    new AzureTenantStore(swagger.tenantId, {
        name: swagger.displayName || swagger.defaultDomain || swagger.tenantId,
        defaultDomain: swagger.defaultDomain
    });

/**
 * Creates subscriptions store from the api received subscription object.
 *
 * @param azureSubscriptionSwagger The subscription api object received from the api.
 * @returns a subscriptions store item.
 */
export const subscriptionSwaggerToSubscriptionStore = (swagger: AzureSubscriptionSwagger) =>
    new AzureSubscriptionStore(swagger.subscriptionId, {
        path: swagger.id,
        name: swagger.displayName
    });

/**
 * Creates an resource group store from the api received resource group object.
 *
 * @param  azureResourceGroupSwagger The resource group object received from the api.
 * @returns An resource group store item.
 */
export const resourceGroupSwaggerToResourceGroupStore = (swagger: AzureResourceGroupSwagger) =>
    new AzureResourceGroupStore(swagger.id, {
        path: swagger.id,
        name: swagger.name,
        location: swagger.location
    });

/**
 * Creates an azure cognitive service account keys store from the api received resource object.
 *
 * @param AzureCsAccountSwagger The resource api object received from the api.
 * @returns A resource store item.
 */
export const csAccountSwaggerToCSAccountStore = (swagger: AzureCsAccountSwagger, keyStrings?: { key1: string; key2: string }) => {
    /**
     * In some cases Azure returns the regions in a non
     * traditional format. Traditional format is lower cased
     * with no spaces. Ex: eastus2, westus, australiaeast, etc.
     *
     * The non traditional format returns the region as "East Us 2"
     * for example.
     *
     * This function normalizes this format into the one that the
     * system is expecting.
     *
     * @param region The region received from the API.
     */
    const normalizeRegion = (region: Region): Region => {
        if (!region) {
            return region;
        }

        return <Region>region
            .toLocaleLowerCase()
            .split(' ')
            .join('');
    };

    return new AzureCsAccountStore(swagger.properties?.internalId ?? swagger.id, {
        path: swagger.id,
        name: swagger.name,
        kind: swagger.kind,
        identityType: swagger.identity?.type,
        region: normalizeRegion(swagger.location),
        principalId: swagger.identity?.principalId,
        endpoint: swagger.properties?.endpoint ?? '',
        keyStrings: keyStrings ?? { key1: '', key2: '' },
        customDomain: swagger.properties?.customSubDomainName ?? null,
        isIdentityEnabled: swagger.identity?.type === 'SystemAssigned' || swagger.identity?.type === 'SystemAssigned, UserAssigned',
        providers: swagger.type.split('/')[0] as AzureCsAccountProviders,
        ownedStorageId: swagger.properties?.userOwnedStorage?.[0]?.resourceId ?? swagger.properties?.storageAccount,
        ownedSearchResourceId: swagger.properties?.apiProperties?.qnaAzureSearchEndpointId,
        ownedAzureOpenAiResourceId: swagger.properties?.apiProperties?.azureOpenAIAccountId,
        sku: {
            code: swagger.sku.name,
            name: LuisConstants.Azure.CsFreeSkuCodes.some(skuCode => swagger.sku.name === skuCode) ? 'Free' : 'Standard'
        }
    });
};

/**
 * Creates an azure search service store from the api received service object.
 *
 * @param AzureSearchServiceSwagger The service api object received from the api.
 * @returns A service store item.
 */
export const azureSearchServiceSwaggerToAzureSearchStore = (swagger: AzureSearchServiceSwagger) => {
    /**
     * In some cases Azure returns the regions in a non
     * traditional format. Traditional format is lower cased
     * with no spaces. Ex: eastus2, westus, australiaeast, etc.
     *
     * The non traditional format returns the region as "East Us 2"
     * for example.
     *
     * This function normalizes this format into the one that the
     * system is expecting.
     *
     * @param region The region received from the API.
     */
    const normalizeRegion = (region: Region): Region => {
        if (!region) {
            return region;
        }

        return <Region>region
            .toLocaleLowerCase()
            .split(' ')
            .join('');
    };

    return new AzureSearchStore(swagger.id, {
        name: swagger.name,
        path: swagger.id,
        location: normalizeRegion(swagger.location),
        sku: swagger.sku
    });
};

/**
 * Gets the underlying token from the received response.
 *
 * @param tokenSwagger The received response.
 * @returns The token string.
 */
export const tokenSwaggerToTokenString = (tokenSwagger: TokenSwagger): string => tokenSwagger.accessToken;

/**
 * Creates an azure storage account store from the api received storage account object.
 *
 * @param storageAccountsSwagger The storage account object received from the api.
 * @returns A storage accounts store items.
 */
export const storageAccountsSwaggerToStorageAccountStore = (
    swagger: AzureStorageAccountSwagger,
    tokenSource: AzureStorageAccountStore['tokenSource']
) =>
    new AzureStorageAccountStore(swagger.id, {
        tokenSource,
        sku: swagger.sku,
        name: swagger.name,
        location: swagger.location,
        azureStorageKind: swagger.kind
    });

/**
 * Creates an azure blob containers store from the api received blob container object.
 *
 * @param blobContainerSwagger The blob container object received from the api.
 * @returns A blob container store items.
 */
export const blobContainerSwaggerToBlobContainerStore = (swagger: AzureContainerSwagger) =>
    createAzureBlobContainerStore(swagger.id, { name: swagger.name, publicAccess: swagger.properties.publicAccess });

/**
 * Creates an azure blob store from the api received blob object.
 */
export const blobSwaggerToAzureBlobStore = (swagger: AzureBlobSwagger) =>
    new AzureBlobStore(swagger.name, {
        ...swagger,
        size: Number(swagger.size),
        blobType: swagger.blobType,
        creationDate: swagger.creationDate && new Date(swagger.creationDate),
        modificationDate: swagger.lastModified && new Date(swagger.lastModified)
    });
